import React, { useEffect, useMemo, useState } from 'react';
import { pickHeroSlides, products as baseProducts } from '../mock';
import { ArrowRight, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { rememberHomeScroll } from './ScrollToTop';
import { useCatalogue } from '../context/CatalogueContext';

export default function HeroBanners() {
  const { products } = useCatalogue();
  const slides = useMemo(() => pickHeroSlides(4), []);
  const [i, setI] = useState(0);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (hovered) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % slides.length), 6500);
    return () => clearInterval(t);
  }, [hovered, slides.length]);

  const slide = slides[i];
  const product =
    products.find((p) => p.id === slide.productId) ||
    baseProducts.find((p) => p.id === slide.productId) ||
    products[0] ||
    baseProducts[0];
  const n = slides.length;
  const [loaded, setLoaded] = useState(() => ({ 0: true }));
  useEffect(() => {
    setLoaded((prev) => ({ ...prev, [i]: true, [(i + 1) % n]: true }));
  }, [i, n]);
  const shouldLoad = (idx) => Boolean(loaded[idx]);

  const sceneBg = {
    background: `
      radial-gradient(ellipse 70% 55% at 78% 58%, ${slide.accent}66 0%, transparent 58%),
      radial-gradient(ellipse 50% 40% at 20% 30%, #FBF6EC 0%, transparent 55%),
      linear-gradient(115deg, #F5EFE6 0%, #F0E6D4 38%, #E8D9C0 62%, #D4C2A4 100%)
    `,
  };

  return (
    <section className="relative overflow-hidden">
      {/* ========== MOBILE: one continuous hero panel ========== */}
      <div className="md:hidden w-full">
        <div
          className="relative w-full overflow-hidden transition-[background] duration-700"
          style={{ aspectRatio: '3 / 4', width: '100%', ...sceneBg }}
        >
          {/* Soft floor so the bottle feels grounded in the scene */}
          <div
            className="absolute inset-x-0 bottom-0 h-[42%] pointer-events-none"
            style={{
              background: `linear-gradient(180deg, transparent 0%, ${slide.accent}18 35%, #1A233218 100%)`,
            }}
          />
          <div
            className="absolute left-1/2 bottom-[14%] -translate-x-1/2 w-[70%] h-10 rounded-[100%] blur-2xl pointer-events-none opacity-50"
            style={{ background: '#1A2332' }}
          />

          {slides.map((s, idx) => (
            shouldLoad(idx) ? (
              <img
                key={s.productId}
                src={s.bottle}
                alt={s.title}
                width={720}
                height={960}
                className={`absolute left-1/2 bottom-[8%] -translate-x-1/2 h-[72%] w-auto max-w-[88%] object-contain object-bottom transition-all duration-700 ease-out ${
                  idx === i ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.96]'
                }`}
                loading={idx === 0 ? 'eager' : 'lazy'}
                decoding={idx === 0 ? 'async' : 'async'}
                fetchPriority={idx === 0 ? 'high' : 'low'}
              />
            ) : null
          ))}

          {/* Text fades into page cream so copy below feels attached */}
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#F5EFE6] via-[#F5EFE6]/70 to-transparent pointer-events-none" />

          <button
            onClick={() => setI((i - 1 + slides.length) % slides.length)}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 shadow-md flex items-center justify-center z-20 touch-manipulation"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setI((i + 1) % slides.length)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 shadow-md flex items-center justify-center z-20 touch-manipulation"
            aria-label="Next slide"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div key={i} className="fade-up bg-[#F5EFE6] px-4 pb-7 pt-1 w-full -mt-2 relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-px w-6 bg-[#C9A961]" />
            <span className="text-[9px] tracking-[0.35em] uppercase text-[#8a6f2d]">The Signature Edit</span>
          </div>
          <h1 className="font-serif-display text-[2rem] leading-[1.05] text-[#1A2332] mb-2">{slide.title}</h1>
          <p className="font-serif-body text-base text-[#1A2332]/80 italic mb-3 leading-snug">{slide.subtitle}</p>
          <div className="flex items-center gap-1 text-[11px] text-[#1A2332]/75 mb-4">
            {[1, 2, 3, 4, 5].map((x) => (
              <Star key={x} className="w-3 h-3 fill-[#C9A961] text-[#C9A961]" />
            ))}
            <span className="ml-1.5">
              {product.rating} · {product.reviews} reviews
            </span>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <Link
              to={`/products/${product.slug}`}
              onClick={() => rememberHomeScroll()}
              className="group inline-flex items-center gap-2 bg-[#1A2332] text-[#F5EFE6] px-5 py-3 text-[11px] tracking-[0.2em] uppercase touch-manipulation"
            >
              Shop Now
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <div className="flex items-baseline gap-2">
              <span className="font-serif-display text-xl text-[#1A2332]">₹{product.price}</span>
              <span className="text-xs line-through text-[#1A2332]/45">₹{product.mrp}</span>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/55">
            <span>{product.type}</span>
            <span className="text-[#C9A961]">•</span>
            <span>{product.gender}</span>
            <span className="text-[#C9A961]">•</span>
            <span>{product.family}</span>
          </div>
          <div className="mt-5 flex justify-center gap-2">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setI(idx)}
                aria-label={`Go to slide ${idx + 1}`}
                className={`h-1.5 rounded-full transition-all touch-manipulation ${idx === i ? 'w-8 bg-[#1A2332]' : 'w-2 bg-[#1A2332]/30'}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ========== DESKTOP: bottle lives inside the same framed scene ========== */}
      <div
        className="hidden md:block max-w-7xl mx-auto px-6 pt-10"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div
          className="relative rounded-lg overflow-hidden shadow-lg hero-frame transition-[background] duration-700"
          style={{ minHeight: '520px', ...sceneBg }}
        >
          {/* Atmosphere layers — same plane as the bottle */}
          <div
            className="absolute inset-0 pointer-events-none transition-opacity duration-700"
            style={{
              background: `
                radial-gradient(ellipse 48% 70% at 72% 55%, ${slide.accent}55 0%, transparent 65%),
                radial-gradient(ellipse 35% 28% at 72% 88%, #1A233240 0%, transparent 70%)
              `,
            }}
          />
          <div className="absolute inset-y-0 left-0 w-[58%] bg-gradient-to-r from-[#F5EFE6] via-[#F5EFE6]/88 to-transparent pointer-events-none z-[1]" />

          {/* Ground ellipse under bottle */}
          <div
            className="absolute right-[8%] bottom-[10%] w-[42%] h-16 rounded-[100%] blur-3xl pointer-events-none opacity-40 z-[1]"
            style={{ background: '#1A2332' }}
          />

          {slides.map((s, idx) => (
            <div
              key={s.productId}
              className={`absolute inset-y-0 right-0 w-[54%] z-[2] flex items-end justify-center pb-[2%] pr-[2%] transition-all duration-700 ease-out ${
                idx === i ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
              }`}
            >
              {shouldLoad(idx) && (
                <img
                  src={s.bottle}
                  alt={s.title}
                  width={900}
                  height={1200}
                  className="h-[92%] w-auto max-w-[92%] object-contain object-bottom select-none"
                  style={{
                    filter: 'drop-shadow(0 28px 36px rgba(26,35,50,0.28))',
                  }}
                  loading={idx === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  fetchPriority={idx === 0 ? 'high' : 'low'}
                />
              )}
            </div>
          ))}

          <div className="absolute top-6 left-6 w-10 h-10 border-l border-t border-[#C9A961]/70 z-20" />
          <div className="absolute top-6 right-6 w-10 h-10 border-r border-t border-[#C9A961]/70 z-20" />
          <div className="absolute bottom-6 left-6 w-10 h-10 border-l border-b border-[#C9A961]/70 z-20" />
          <div className="absolute bottom-6 right-6 w-10 h-10 border-r border-b border-[#C9A961]/70 z-20" />

          <div className="relative z-10 h-full w-full flex items-center min-h-[520px]">
            <div key={`desk-${i}`} className="fade-up max-w-[520px] px-14 py-16">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-8 bg-[#C9A961]" />
                <span className="text-[10px] tracking-[0.5em] uppercase text-[#8a6f2d]">The Signature Edit</span>
              </div>
              <h1 className="font-serif-display text-7xl leading-[0.98] text-[#1A2332] mb-5">{slide.title}</h1>
              <p className="font-serif-body text-2xl text-[#1A2332]/80 italic mb-6 max-w-md leading-relaxed">
                {slide.subtitle}
              </p>
              <div className="flex items-center gap-1 text-xs text-[#1A2332]/75 mb-7">
                {[1, 2, 3, 4, 5].map((x) => (
                  <Star key={x} className="w-3.5 h-3.5 fill-[#C9A961] text-[#C9A961]" />
                ))}
                <span className="ml-2">
                  {product.rating} · {product.reviews} verified reviews
                </span>
              </div>
              <div className="flex flex-wrap gap-4 items-center">
                <Link
                  to={`/products/${product.slug}`}
                  onClick={() => rememberHomeScroll()}
                  className="group inline-flex items-center gap-2 bg-[#1A2332] text-[#F5EFE6] px-7 py-4 text-[12px] tracking-[0.25em] uppercase hover:bg-[#2c3e5b] transition-colors shadow-lg"
                >
                  Shop Now
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <div className="flex items-baseline gap-2">
                  <span className="font-serif-display text-2xl text-[#1A2332]">₹{product.price}</span>
                  <span className="text-sm line-through text-[#1A2332]/45">₹{product.mrp}</span>
                </div>
              </div>
              <div className="mt-7 flex items-center gap-4 text-[11px] tracking-[0.25em] uppercase text-[#1A2332]/60">
                <span>{product.type}</span>
                <span className="text-[#C9A961]">•</span>
                <span>{product.gender}</span>
                <span className="text-[#C9A961]">•</span>
                <span>{product.family}</span>
              </div>
            </div>
          </div>

          <button
            onClick={() => setI((i - 1 + slides.length) % slides.length)}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition z-20"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setI((i + 1) % slides.length)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition z-20"
            aria-label="Next slide"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setI(idx)}
                aria-label={`Go to slide ${idx + 1}`}
                className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-10 bg-[#1A2332]' : 'w-2 bg-[#1A2332]/30 hover:bg-[#1A2332]/50'}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
